import React from "react";
import { App, Avatar, Button, List, Modal, Popconfirm, Space, Tag, Typography } from "antd";
import { statusMeta, currency } from "./subscriptionDrawerUtil";
import api from "../../services/api.js";
import { LicenseRow } from "src/reducers/subscription";
import { useLanguage } from "src/providers/LanguageProvider";

const { Text } = Typography;

const lineStyle = (level: number) => ({
  marginTop: 8,
  marginBottom: 8,
  marginLeft: level === 0 ? 0 : 12,
  paddingLeft: level === 0 ? 0 : 12,
  borderLeft: level === 0 ? "none" : "1px dashed #e5e5e5",
});

function isMonthlyFromInterval(interval?: string | null): boolean {
  if (!interval) return false;
  const s = String(interval).toLowerCase();
  // çok dilli olabilecek yaygın kalıplar
  return /\bay|\bmonth|\bmo|\bmonat|\bmaand/.test(s);
}

/**
 * Tek bir lisans öğesini ve varsa çocuklarını render eder (recursive).
 */
function LicenseNode({
  item,
  level = 0,
  onCancelChild,
}: {
  item: any;
  level?: number;
  onCancelChild?: (e: LicenseRow) => void;
}) {
  const { t } = useLanguage();
  const { message } = App.useApp();
  const [loadingCancel, setLoadingCancel] = React.useState(false);

  const isMonthly =
    typeof item?.isMonthly === "boolean"
      ? !!item.isMonthly
      : isMonthlyFromInterval(item?.interval);

  const handleCancelLicense = async (e: LicenseRow) => {
    setLoadingCancel(true);
    try {
      await api.get("/licenses/cancel/" + e.Id);
      message.success(t("SUBSCRIPTION_CANCELLED"));
      onCancelChild?.(e);
    } catch (err) {
      console.error(err);
      message.error(t("SUBSCRIPTION_CANCEL_FAILED"));
    } finally {
      setLoadingCancel(false);
    }
  };

  // --- YENİ EKLENEN KISIM BAŞLANGIÇ ---
  // ProductId 21 ise ve kullanıcı Active ise iptal işlemi engellenir.
  const isProduct21 = item.licenseProductId == 21;
  const isUserActive = item.wUserInfo?.IsActive;

  // İptal butonu şu durumlarda disable (pasif) olacak:
  // 1. Zaten iptal edilmişse (item.status === "canceled")
  // 2. VEYA (Product 21 İSE VE Kullanıcı Aktif İSE)
  const isCancelDisabled = item.status === "canceled" || (isProduct21 && isUserActive);
  // --- YENİ EKLENEN KISIM BİTİŞ ---

  const actions = [
    <Tag key="st" color={statusMeta[item.status]?.color || "default"}>
      {t(statusMeta[item.status]?.text || item.status || "")}
    </Tag>,
    !item.isRequired ? (
      <Popconfirm
        key="cancel"
        title={t("CONFIRM_CANCEL_CHILD_TITLE")}
        description={t("CONFIRM_CANCEL_CHILD_DESC")}
        okText={t("YES")}
        cancelText={t("NO")}
        onConfirm={() => handleCancelLicense(item)}
        // Popconfirm tetiklenmesini de engellemek için:
        disabled={isCancelDisabled} 
      >
        <Button 
            loading={loadingCancel} 
            size="small" 
            danger 
            // Butonun görünürde pasif olması için:
            disabled={isCancelDisabled}
        >
          {t("BTN_CANCEL")}
        </Button>
      </Popconfirm>
    ) : null,
  ].filter(Boolean);

  return (
    <div style={lineStyle(level)}>
      <List.Item actions={actions as any[]} key={item.key || item.Id}>
        <List.Item.Meta
          avatar={<Avatar shape="square" src={item.img} />}
          title={
            <Space size={6} wrap>
              <Text strong>{item.service}</Text>
              {item.licenseProductId == 21 && item.wUserInfo ? (
                <Text >
                  {item.wUserInfo.FirstName} {item.wUserInfo.LastName} {item.wUserInfo.Email}
                  {item.wUserInfo.IsActive ? (
                    <Tag color="success" style={{ marginLeft: 4 }}>
                      {t("ACTIVE")}
                    </Tag>
                  ) : (
                    <Tag color="error" style={{ marginLeft: 4 }}>
                      {t("INACTIVE")}
                    </Tag>
                  )}
                </Text>
              ) : null}
              <Text type="secondary">•</Text>
              <Text type="secondary">{isMonthly ? t("MONTHLY") : t("ONE_TIME")}</Text>

            </Space>
          }
          description={
            <Space direction="vertical" size={0}>
              <Text type="secondary">
                {t("AMOUNT_LABEL")}: {currency(item.amount)} {isMonthly ? ` ${t("PER_MONTH_SHORT")}` : ""}
              </Text>
              {typeof item.totalAmount === "number" ? (
                <Text type="secondary">
                  {t("TOTAL_LABEL")}: {currency(item.totalAmount)}
                </Text>
              ) : null}
              {item.nextRenewal ? (
                <Text type="secondary">
                  {t("NEXT_RENEWAL")}: {item.nextRenewal}
                </Text>
              ) : null}
            </Space>
          }
        />
      </List.Item>

      {Array.isArray(item.children) && item.children.length > 0 ? (
        <List
          itemLayout="horizontal"
          dataSource={item.children}
          renderItem={(child: any) => (
            <LicenseNode
              key={child.key || child.Id}
              item={child}
              level={level + 1}
              onCancelChild={onCancelChild}
            />
          )}
        />
      ) : null}
    </div>
  );
}

const SubscriptionDrawerManageGroupModal: React.FC<{
  open: boolean;
  group: any;
  onClose: () => void;
  onCancelChild: (child: LicenseRow) => void;
}> = ({ open, group, onClose, onCancelChild }) => {
  const { t } = useLanguage();

  const groupIsMonthly =
    typeof group?.isMonthly === "boolean"
      ? !!group.isMonthly
      : isMonthlyFromInterval(group?.interval) ||
      (Array.isArray(group?.items) && isMonthlyFromInterval(group.items[0]?.interval));

  const title = group
    ? `${t("MANAGE_CHILD_LICENSES")} • ${group.plan} (${groupIsMonthly ? t("MONTHLY") : t("ONE_TIME")})`
    : t("MANAGE_CHILD_LICENSES");

  return (
    <Modal title={title} open={open} onCancel={onClose} footer={null} destroyOnClose>
      {group ? (
        <List
          itemLayout="horizontal"
          dataSource={group.items}
          renderItem={(item: any) => (
            <LicenseNode key={item.key || item.Id} item={item} level={0} onCancelChild={onCancelChild} />
          )}
        />
      ) : null}
    </Modal>
  );
};

export default SubscriptionDrawerManageGroupModal;
